import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Observable, Subject, Subscriber, Subscription, takeUntil } from 'rxjs';
import { Quote } from '../../../models/quote';
import { ShipmentService } from '../../../data/shipment.service';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
    selectShipmentEditorCategory,
    selectShipmentEditorId,
    selectShipmentEditorShipment
} from '../../../store/shipment-editor/shipment-editor.state';
import {
    selectFocusedQuoteId,
    selectQuotingActiveShipments,
    shipmentQuotingProgress
} from '../../../store/quote-progress/quote-progress.state';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { quotingFocus } from '../../../store/quote-progress/quote-progress.actions';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ElementObserverService } from '../../shared/services/element-observer.service';
import { QuoteCardComponent } from '../quote-card/quote-card.component';
import { Shipment } from '../../../models/shipment';
import { ShipmentSubmitComponent } from '../shipment-submit/shipment-submit.component';
import { ServerMessage } from '../../../models/server-message';
import { MatDialog } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { IconComponent } from '../../shared/icon/icon.component';
import { PriceBreakdownComponent } from '../../shared/price-breakdown/price-breakdown.component';
import { ServerMessageComponent } from '../../shared/server-message/server-message.component';
import { AsyncPipe } from '@angular/common';
import { ProgressBackgroundComponent } from '../progress-background/progress-background.component';
import { Notice } from '../../../models/notice';
import { selectProfileNotices } from '../../../store/profile/profile.state';
import { NoticeTypeEnum } from '../../../enum/notice-type.enum';
import { ProfileNoticeComponent } from '../../shared/profile-notice/profile-notice.component';
import { ContextNoticesComponent } from '../../navigation/context-notices/context-notices.component';
import { LocaleService } from '../../shared/services/locale.service';
import { noServiceNotice } from '../../../constants/no-service-notice';
import { ActivatedRoute, Router } from '@angular/router';
import { selectRouteParam, selectRouteParamDeep } from '../../../store/router/router.state';
import { ShipmentCategoryEnum } from '../../../enum/shipment-category.enum';

interface QuoteCardData {
    quote: Quote;
    x: string;
    y: string;
    selected: boolean;
    transform: string;
    infoX: string;
    infoWidth: string;
}

@Component({
    selector: 'app-shipment-quoting',
    templateUrl: './shipment-quoting.component.html',
    styleUrls: [ './shipment-quoting.component.scss' ],
    animations: [
        trigger('card', [
            transition(':enter', [
                style({
                    opacity: 0,
                    top: '-50px'
                }),
                animate('0.75s ease-out'),
                style({
                    opacity: 1,
                    top: 0
                })
            ]),
            transition(':leave', [
                style({
                    opacity: 1,
                    top: 0
                }),
                animate('0.75s ease-in'),
                style({
                    opacity: 0,
                    top: '-50px'
                })
            ]),
        ])
    ],
    imports: [ ProgressBackgroundComponent, ServerMessageComponent, PriceBreakdownComponent, IconComponent, MatButton, QuoteCardComponent,
        AsyncPipe, ProfileNoticeComponent, ContextNoticesComponent ]
})
export class ShipmentQuotingComponent implements OnInit, AfterViewInit, OnDestroy {

    private CARD_GUTTER = 16;

    public displayQuotes$: Subject<QuoteCardData[]> = new Subject<QuoteCardData[]>();

    @ViewChild('cardDisplay')
    private gridElement: ElementRef;

    private containerWidth$: Observable<number>;
    private quoteSubscription: Subscription;
    public shipment: Shipment;

    private destroy$ = new Subject<void>();

    constructor(private shipmentService: ShipmentService, private store: Store<AppState>, private breakpointObserver: BreakpointObserver,
                private elementObserver: ElementObserverService, private elementRef: ElementRef, private dialog: MatDialog,
                private locale: LocaleService, private router: Router, private route: ActivatedRoute) {
    }

    shipmentId$ = this.store.select(selectRouteParamDeep('id'));

    quotes$ = this.shipmentId$.pipe(
        filter((id) => !!id),
        switchMap((id: string) => combineLatest([
            this.shipmentService.observe(id), this.store.select(shipmentQuotingProgress(id))
        ])),
        tap(([ shipment, progress ]) => console.log(shipment, progress)),
        filter(([ shipment, ]) => !!shipment),
        tap(([ shipment, ]) => this.shipment = shipment),
        map(([ shipment, progress ]) => (progress && progress.busy) ? progress.quotes : shipment.quotes),
        map((quotes) => quotes.filter(q => q.customer && q.customer.net > 0)),
        map((quotes) => [ ...quotes.sort((a, b) => a.customer.net - b.customer.net) ])
    );

    private quoting$ = this.shipmentId$.pipe(
        switchMap((id) => this.store.select(shipmentQuotingProgress(id))),
        filter(progress => !!progress)
    );

    public progress$ = this.quoting$.pipe(
        map(progress => progress.progress)
    );

    public messages$ = this.quoting$.pipe(
        map(progress => progress.messages),
        map(messages => messages.map(m => this.locale.serverMessageToNotice(m)))
    );

    public displayProgress$ = this.quoting$.pipe(
        map(progress => progress.busy),
        startWith(false)
    );

    public notices$ = combineLatest([
        this.messages$,
        this.store.select(selectProfileNotices([ NoticeTypeEnum.QUOTE ]))
    ]).pipe(
        map(([ messages, systemNotices ]) => [ ...messages, ...systemNotices ])
    );

    public noServiceNotices$ = combineLatest([
        this.displayProgress$,
        this.quotes$,
        this.store.select(selectProfileNotices([ 
            NoticeTypeEnum.NO_SERVICES_AVAILABLE,
            NoticeTypeEnum.NO_SERVICES_AVAILABLE_SMALL_PARCEL,
            NoticeTypeEnum.NO_SERVICES_AVAILABLE_LTL
        ])),
        this.store.select(selectShipmentEditorCategory)
    ]).pipe(
        tap(() => console.log('no service notice observable')),
        map(([ displayProgress, quotes, notices, category ]) => {
            if (!displayProgress && quotes.length === 0) {
                // Determine the preferred notice type based on category
                const preferredType = category === ShipmentCategoryEnum.LTL 
                    ? NoticeTypeEnum.NO_SERVICES_AVAILABLE_LTL
                    : NoticeTypeEnum.NO_SERVICES_AVAILABLE_SMALL_PARCEL;
                
                // Filter notices by preferred type
                const categoryNotices = notices.filter(n => n.type === preferredType);
                
                // If category-specific notices exist, use them; otherwise fall back to general notices
                if (categoryNotices.length > 0) {
                    return categoryNotices;
                }
                
                // Fall back to general NO_SERVICES_AVAILABLE notices
                const generalNotices = notices.filter(n => n.type === NoticeTypeEnum.NO_SERVICES_AVAILABLE);
                return generalNotices.length === 0 ? [ noServiceNotice ] : generalNotices;
            } else {
                return [];
            }
        })
    );

    quotingStarted$ = this.quoting$.pipe(
        map(p => p.progress > 0 || p.busy),
        startWith(false),
        distinctUntilChanged()
    );

    ngOnInit(): void {
        this.route.queryParamMap
            .pipe(
                map(params => params.get('quoteId')),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(id => this.store.dispatch(quotingFocus({ id })));
        this.clearFocus();
    }

    quoteTrack(index, item): string {
        return item && item.quote ? item.quote.id : '';
    }

    selectQuote(quote?: Quote): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { quoteId: quote?.id },
            queryParamsHandling: 'merge', // keep other params if any
        });
    }

    clearFocus(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { quoteId: null },
            queryParamsHandling: 'merge',
        });
    }

    filterQuotes(quotes: Quote[], focusedId: string): Quote[] {
        return focusedId ? quotes.filter((quote) => quote.id === focusedId) : quotes;
    }

    positionQuote(quote: Quote, index: number, width: number, focusedId: string): QuoteCardData {
        let scale = 1;
        if (width < 600) {
            scale = 0.55;
        }
        const cardWidth = QuoteCardComponent.WIDTH * scale;
        const cardHeight = QuoteCardComponent.HEIGHT * scale;
        const top = (1 - scale) * -cardHeight;
        const left = (1 - scale) * -cardWidth;
        const cardPerRow = Math.floor(width / (cardWidth + this.CARD_GUTTER));
        const marginLeft = Math.floor((width - (cardPerRow * (cardWidth + this.CARD_GUTTER))) / 2);
        const column = index % cardPerRow;
        const row = Math.floor(index / cardPerRow);
        const x = left + marginLeft + (column * (cardWidth + this.CARD_GUTTER));
        const y = top + row * (cardHeight + this.CARD_GUTTER);
        return {
            quote,
            x: x + 'px',
            y: y + 'px',
            selected: quote.id === focusedId,
            transform: scale === 1 ? 'none' : 'scale(' + scale + ')',
            infoX: (marginLeft + cardWidth + 20) + 'px',
            infoWidth: (width - (marginLeft * 2 + cardWidth + 20)) + 'px'
        };
    }

    ngAfterViewInit(): void {
        this.containerWidth$ = this.elementObserver.observeWidth(this.gridElement);
        setTimeout(() => {
            this.quoteSubscription = combineLatest([ this.quotes$, this.store.select(selectFocusedQuoteId), this.containerWidth$ ]).pipe(
                map(([ quotes, focusedId, width ]) => ({ quotes: this.filterQuotes(quotes, focusedId), width, focusedId })),
                map(({
                         quotes,
                         width,
                         focusedId
                     }) => quotes.map((quote, index) => this.positionQuote(quote, index, width, focusedId)))
            ).subscribe((quotes) => this.displayQuotes$.next(quotes));
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        if (this.quoteSubscription) {
            this.quoteSubscription.unsubscribe();
            this.quoteSubscription = null;
        }
    }

    ship(quote: Quote): void {
        this.dialog.open(ShipmentSubmitComponent, { data: { shipment: this.shipment, quote } });
    }

}
